import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from 'react';
import { Table, Input, FormGroup, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFolderOpen, faPenToSquare, faTrashCan } from '@fortawesome/free-solid-svg-icons';

import { translations } from './../../i18/translations.js';
import Search from './../../util/Search.js';
import Pager from './../../util/Pager.js';
import { Space } from './../../util/Html.js';
import ConfirmDialog from './../../util/ConfirmDialog.js';

const MenuManagement = () => {

    const API_URL = process.env.REACT_APP_SERVICE_API_URL;
    const {user, lang, showAlert, checkAccess} = useOutletContext();
    const {parentId} = useParams();
    const navigate = useNavigate();

    const [query, setQuery] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [showOld, setShowOld] = useState(false);
    const [selectedParentId, setSelectedParentId] = useState(parentId);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [template, setTemplate] = useState('');
    const [pager, setPager] = useState([]);

    const [parentList, setParentList] = useState([]);
    const [menus, setMenus] = useState([]);
    const [selectedMenu, setSelectedMenu] = useState(null);

    const pageSize = 20;

    useEffect(() => {
        fetchMenus(pageIndex, query, showOld, selectedParentId);
    }, [pageIndex, query, showOld, selectedParentId, lang, user]);

    const toggleConfirmDialog = () => setConfirmOpen(!confirmOpen);

    const fetchMenus = async (pageIndex, query, showOld, selectedParentId) => {
        try {
            const payload = {
                filter: {
                    pageSize: 20,
                    pageIndex: pageIndex,
                    queryString: query,
                    showInvisible: showOld,
                    parentId: selectedParentId
                }
            };

            const response = await fetch(`${API_URL}/admin/get_menu_data.php`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (checkAccess(data)) {
                setMenus(data.data);
                setPager(data.pagerButtonIndexes);
                setTemplate(data.template);
                setParentList(data.parentList);
            }
        } catch (error) {
            console.error("Error fetching contents:", error);
        }
    };

    const openMenu = (id) => {
        setSelectedParentId(id);
    };

    const editMenu = (id) => {
        navigate("/MenuManagementEdit/" + selectedParentId + "/" + id);
    };

    const handleDeleteMenuClick = async (selectedMenu) => {
        setSelectedMenu(selectedMenu);
        toggleConfirmDialog();
    };

    const deleteMenu = async () => {
        try {
            const payload = {
                menu: {
                    id: selectedMenu.id
                }
            };

            const response = await fetch(`${API_URL}/admin/delete_menu.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (checkAccess(data)) {
                showAlert(data.alert);
                fetchMenus(pageIndex, query, showOld, selectedParentId);
                toggleConfirmDialog();
            }
        } catch (error) {
            console.error("Error fetching members:", error);
        }
    };

    const handleShowOldSwitch = (e) => {
        const {checked} = e.target;
        setShowOld(checked);
    };

    return (
            <div className="mbkegy-content">
                <h1 className="mbkegy-content">{translations[lang].menu_management}</h1>
            
                {parentList.map((p) => (
                                <>
                                &nbsp;/&nbsp;<Button className="text-nowrap" color="link" onClick={() => setSelectedParentId(p.id)}>{p.label}</Button>
                                </>
                                ))}
            
                <div className="membership-management-filter-container mb-3">
                    <Search placeholder={translations[lang].search_menu} handleSearch={setQuery} />
                    <Button className="text-nowrap" color="success" title={translations[lang].anew} onClick={() => editMenu(-1)}>
                        <FontAwesomeIcon icon={faPlus} />
                    </Button>
                    <FormGroup switch>
                        <Input id="showOld" type="switch" checked={showOld} onChange={handleShowOldSwitch} />
                        <Label for="showOld" className="text-nowrap">{translations[lang].show_invisible}</Label>
                    </FormGroup>
            
                </div>
            
                <Table striped className="mbkegy-table">
                    <thead>
                        <tr>
                            <th style={{width: '75px'}}>{translations[lang].col_id}</th>
                            <th style={{width: '200px'}}>{translations[lang].col_label}</th>
                            <th style={{width: '65%'}}>{translations[lang].col_content}</th>
                            <th style={{width: '50px'}}>{translations[lang].col_feature}</th>
                            <th style={{width: '20px'}}>{translations[lang].col_visible}</th>
                            <th style={{width: '110px'}}>&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {menus.map((m) => (
                                <tr key={m.id}>
                                    <td>{m.label ? m.id : <Space />}</td>
                            <td>{m.label || <Space />}</td>
                            <td>{m.content || <Space />}</td>
                            <td>{m.access_feature || <Space />}</td>
                            <td>{m.visible || <Space />}</td>
                            <td className="text-nowrap">
                                {m.label && <Button outline size="sm" onClick={() => openMenu(m.id)}><FontAwesomeIcon icon={faFolderOpen} /></Button>}
                                {m.label && <Button outline size="sm" color="primary" onClick={() => editMenu(m.id)}><FontAwesomeIcon icon={faPenToSquare} /></Button>}
                                {m.label && <Button outline size="sm" color="danger" onClick={() => handleDeleteMenuClick(m)}><FontAwesomeIcon icon={faTrashCan} /></Button>}
                            </td>
                            </tr>
                                    ))}
                    </tbody>
                </Table>
            
                <Pager template={`${translations[lang].rows}: ${template}`} pager={pager} handlePageChange={setPageIndex} />
            
                <ConfirmDialog 
                    title={translations[lang].menu_delete}
                    yes={translations[lang].yes}
                    no={translations[lang].cancel}
                    isOpen={confirmOpen} 
                    toggle={toggleConfirmDialog} 
                    onConfirm={deleteMenu} 
                    message={translations[lang].menu_delete_confirm}
                    />
            
            </div>
            );
};

export default MenuManagement;
    